import { Box, TableCell, TableRow } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { useEffect, useRef } from "react";
import { ChargeLayer } from "@/pages/sy/models/ChargeLayer";
import { chargeOperationStyles } from "@/pages/sy/styles/ChargeOperationStyles";
import { formatCraneMagnitude } from "../utils/formatCraneMagnitude";

export interface LayerRowProps {
  layer: ChargeLayer;
  commodityColor: string;
  bgColor: string;
  diff: number | null;
  isSelected: boolean;
  styles: ReturnType<typeof chargeOperationStyles>;
  getBgColorWithAlpha: (color: string, alpha: number) => string;
  onStartLongPress: () => void;
  onCancelLongPress: () => void;
  onClick: () => void;
}

export const LayerRow = ({
  layer,
  commodityColor,
  bgColor,
  diff,
  isSelected,
  styles,
  getBgColorWithAlpha,
  onStartLongPress,
  onCancelLongPress,
  onClick,
}: LayerRowProps) => {
  const rowRef = useRef<HTMLTableRowElement>(null);
  const handledByTouchRef = useRef(false);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      handledByTouchRef.current = true;
      onStartLongPress();
    };

    const handleTouchEnd = () => {
      onCancelLongPress();
      onClick();
    };

    const handleContextMenu = (e: Event) => {
      e.preventDefault();
    };

    row.addEventListener("touchstart", handleTouchStart, { passive: false });
    row.addEventListener("touchend", handleTouchEnd);
    row.addEventListener("contextmenu", handleContextMenu);

    return () => {
      row.removeEventListener("touchstart", handleTouchStart);
      row.removeEventListener("touchend", handleTouchEnd);
      row.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [onStartLongPress, onCancelLongPress, onClick]);

  const handleClick = () => {
    // On touch devices, the touch handler already called onClick — skip the synthetic click
    if (handledByTouchRef.current) {
      handledByTouchRef.current = false;
      return;
    }
    onClick();
  };

  const rowSx: SxProps<Theme> = {
    ...(styles.currentLayersTableRow as object),
    ...(isSelected ? (styles.selectedRow as object) : {}),
    backgroundColor: (theme: Theme) =>
      layer.IsCurrent
        ? getBgColorWithAlpha(theme.palette.charges.status.active, 0.4)
        : isSelected
          ? bgColor
          : "inherit",
    cursor: "pointer",
    userSelect: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
  };

  return (
    <TableRow
      ref={rowRef}
      onClick={handleClick}
      onMouseDown={onStartLongPress}
      onMouseUp={onCancelLongPress}
      onMouseLeave={onCancelLongPress}
      sx={rowSx}
    >
      <TableCell align="center">{layer.LayerNumber}</TableCell>
      <TableCell>
        <Box sx={styles.commodityCell}>
          <Box
            sx={{
              ...(styles.colorIndicator as object),
              backgroundColor: commodityColor,
            }}
          />
          {layer.CommodityName}
        </Box>
      </TableCell>
      <TableCell align="center">
        {formatCraneMagnitude(layer.Weight, "0")}
      </TableCell>
      <TableCell
        align="center"
        sx={{
          ...(diff != null && diff > 0 ? (styles.diffCell as object) : {}),
          ...(diff != null && diff < 0 ? (styles.negativeDiff as object) : {}),
        }}
      >
        {diff == null ? "-" : formatCraneMagnitude(diff, "0")}
      </TableCell>
      <TableCell align="center">
        {layer.IsManualEdit ? (
          <Box
            sx={{
              backgroundColor: (theme: Theme) =>
                theme.palette.charges.chargeTypes.manual,
              color: "white",
              borderRadius: "4px",
              width: 28,
              height: 28,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              fontSize: "0.875rem",
              lineHeight: 1,
            }}
          >
            M
          </Box>
        ) : null}
      </TableCell>
    </TableRow>
  );
};
